<template>
  <div class="multiselect-container">
    <validation-provider :rules="computedRules" v-bind="$attrs" v-slot="{ errors }">
      <external-multiselect
        ref="multiselect"
        v-model="model"
        v-bind="$attrs"
        v-on="$listeners"
        selectLabel=""
        selectedLabel="Selecionado"
        deselectLabel=""
        selectGroupLabel=""
        deselectGroupLabel=""
        tagPlaceholder=""
        :openDirection="openDirection"
        @open="onOpen"
        @close="onClose"
        :class="{
          'has-selection': isDropdownOpen && model && model.length > 0,
          'is-multiple': $attrs.multiple,
        }"
      >
        <template v-slot:noResult>Nenhum Resultado Encontrado</template>
        <template v-slot:noOptions>A Lista está Vazia!</template>
        <template v-slot:maxElements>O Máximo de Opções foi Atingido. Remova uma Opção Selecionada para Selecionar Outra.</template>

        <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope"><slot :name="slot" v-bind="scope"></slot></template>
        <template v-slot:afterList>
          <div v-if="loading" class="loading-spinner-container">
            <div class="loading-spinner"></div>
          </div>
        </template>

        <template v-if="selectAll" v-slot:beforeList>
          <div
            @click="toggleSelectAll" 
            :class="allSelected ? 'all-selected ' : 'multiple select-all-option'" 
            :style="allSelected ? 'background-color: #e9ecef; color: #212529' : ''"
          >
            <span :style="allSelected ? 'font-weight: 700' : ''">{{ allSelected ? 'desmarcar todos' : 'selecionar todos' }}</span>
          </div>
        </template>
      </external-multiselect>

      <slot name="error">
        <div v-if="error" class="invalid-feedback" style="display: block;">
          {{ error }}
        </div>
        <div v-else-if="errors[0]" class="invalid-feedback" style="display: block;">
          {{ errors[0] }}
        </div>
      </slot>
    </validation-provider>
  </div>
</template>

<script>
import ExternalMultiselect from "vue-multiselect";

export default {
  name: 'Multiselect',
  components: {
    ExternalMultiselect,
  },
  props: {
    value: {
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false,
    },
    infiniteScroll: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      description: "Input error (below input)"
    },
    openDirection: {
      type: String,
      default: 'bottom',
    },
    rules: {
      type: [String, Array, Object],
      description: 'Vee validate validation rules',
      default: ''
    },
    selectAll: {
      type: Boolean,
      description: 'Permit select all options',
      default: false
    },
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    computedRules() {
      return this.rules ? this.rules : null; 
    },
  },
  mounted() {
    this.setInfiniteScrollEvent();
  },
 
  beforeDestroy() {
    const multiselect = this.$refs.multiselect.$el.querySelector('.multiselect__content-wrapper');
    if (!multiselect) return;

    multiselect.removeEventListener('scroll', this.debouncedOnScroll);
  },
  data() {
    return {
      debounceTimeout: null,
      isDropdownOpen: false,
      updatedHeight: false,
      allSelected: false,
    };
  },
  methods: {
    toggleSelectAll() {
      if (this.allSelected) {
        this.model = [];
      } else {
        this.model = this.$attrs.options;
      }

      this.allSelected = !this.allSelected;
    },
    setInfiniteScrollEvent() {
      const multiselect = this.$refs.multiselect.$el.querySelector('.multiselect__content-wrapper');

      if (!multiselect) {
        console.error('Multiselect content wrapper not found.')
        return;
      }

      multiselect.addEventListener('scroll', this.debouncedOnScroll);
    },
    validateField(value) {
      const hasError = this.errors && this.errors.length > 0;  
      this.$emit('validation-changed', !hasError);
    },
    debouncedOnScroll(event) {
      if (this.loading) return;
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);

      this.debounceTimeout = setTimeout(() => { this.onScroll(event) }, 200);
    },
    onScroll(event) {
      const target = event.target;
      const bottomReached = (target.clientHeight + 200 ) + target.scrollTop >= target.scrollHeight;
      if (!bottomReached) return;

      this.$emit('bottom-reached');
    },
    onOpen() {
      this.isDropdownOpen = true;
    },
    onClose() {
      this.isDropdownOpen = false;
    },
    setWrapperPosition() {
      const multiselect = this.$refs.multiselect.$el.querySelector('.multiselect__content-wrapper');

      if (!multiselect) {
        console.error('Multiselect content wrapper not found.')
        return;
      }

      const position = this.$refs.multiselect.$el.getBoundingClientRect();

      multiselect.style.position = 'fixed';
      multiselect.style.top = position.bottom + 'px';
      multiselect.style.left = position.left + 'px';
      multiselect.style.width = position.width + 'px';

      const qtdOptions = this.$attrs.options.length;
      var height = qtdOptions < 5 ? (qtdOptions || 1) * 41 : 180;
      multiselect.style.height = height + 'px';
      
      if ((window.innerHeight - position.bottom) < height) {
        multiselect.style.top = 'auto';
        multiselect.style.bottom = '0';
      }
    },
  },
}
</script>

<style>
.multiselect-container {
  position: relative;
}

.loading-spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  height: 40px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.has-selection.is-multiple .multiselect__tags {
  min-height: 75px !important; 
}

.multiselect .multiselect__content-wrapper {
  transition: visibility 0.25s, opacity 0.25s, transform 0.25s;
  z-index: 1;
}

.multiple {
  cursor: pointer;
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
  height: 30px;
  padding: 12px 10px 30px 12px;
}
.multiple:hover {
  background-color: #41b883;
  color: white;
}

.multiselect__option--highlight{
  background-color: transparent !important;
  color: #35495e !important;
}

.multiselect__option:hover {
  background-color: #41b883  !important; 
  color: white !important;
}
.multiselect__strong {
  position: relative;
  margin-left: 7px;
  padding: 4px !important;
}

.multiselect__strong-counter {
  position: relative;
  margin-left: 7px;
  padding: 3px 5px 3px 10px;
  color: #FFFFFF;
  font: 12px "Open Sans";
  background-color: #41B883;
  border-radius: 0.25rem;
}
.all-selected:hover {
  background-color: #41b883  !important; 
  color: white;
  height: 30px;
  padding: 12px 10px 30px 12px;
}
.all-selected {
  height: 30px;
  padding: 12px 10px 30px 12px;
  cursor: pointer;
}
.multiselect__option--selected.multiselect__option--highlight {
  background-color: rgb(239, 241, 244) !important;
  color: #35495e !important;
}

</style>